if (!customElements.get('product-options')) {
class ProductOptionPicker extends HTMLElement {
  constructor() {
    super();

    this.PLACEHOLDER = 'Select an option';

    this.section   = this.closest('.main-product');
    this.sectionId = this.section?.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;

    this.refreshRefs();
    this.initializePlaceholders();
    this.installDelegatedEvents();
    this.installOutsideClickHandler();
  }

  /* -------------------- refs & init -------------------- */
  refreshRefs() {
    this.productOptions   = this.section.querySelectorAll('product-options ul');
    this.productOptionsLi = this.section.querySelectorAll('product-options li');
    this.productDropdowns = this.section.querySelectorAll('.product-option-picker__dropdown');
    this.form             = this.section.querySelector('form[action*="/cart/add"]') || this.section.querySelector('form');
  }

  initializePlaceholders() {
    this.section.querySelectorAll('product-options li.chosen').forEach(li => li.classList.remove('chosen'));
    this.productOptions.forEach(ul => {
      this.setDropdownLabel(ul, this.PLACEHOLDER);
      this.setDropdownBoxValue(ul, this.PLACEHOLDER); // keep the “box” not blank
    });
    this.clearVariantAndDisableATC();
    this.syncLabelsFromChosen();
  }

  installDelegatedEvents() {
    if (!this._delegatedDropdownHandler) {
      this._delegatedDropdownHandler = (event) => {
        const dropdown = event.target.closest('.product-option-picker__dropdown');
        if (dropdown && this.section.contains(dropdown)) this.dropdown(dropdown, event);
      };
      this.section.addEventListener('click', this._delegatedDropdownHandler);
    }

    if (!this._delegatedOptionHandler) {
      this._delegatedOptionHandler = (event) => {
        const option = event.target.closest('.product-option-picker__list-item');
        if (option && !option.classList.contains('disabled') && !option.classList.contains('chosen')) {
          if (this.contains(option)) this.onClick(option);
        }
      };
      document.addEventListener('click', this._delegatedOptionHandler);
    }

    // Safety: ensure we write the correct variant id right before submit
    if (this.form && !this._submitFix) {
      this._submitFix = (e) => {
        const idInput = this.getIdInput();
        if (!idInput || !idInput.value) {
          // block accidental stale submit
          e.preventDefault();
          e.stopPropagation();
        }
      };
      this.form.addEventListener('submit', this._submitFix, true);
    }
  }

  installOutsideClickHandler() {
    if (!this._outsideClickHandler) {
      this._outsideClickHandler = (e) => {
        const open = this.section.querySelectorAll('.product-option-picker__dropdown.active');
        if (!open.length) return;
        const inside = e.target.closest('.product-option-picker__dropdown');
        if (!inside || !this.section.contains(inside)) open.forEach(dd => this.closeDropdown(dd));
      };
      document.addEventListener('click', this._outsideClickHandler);
    }
  }

  /* -------------------- utilities (UI) -------------------- */
  getLists() { return Array.from(this.section.querySelectorAll('product-options ul')); }
  getIndex(ul) { return this.getLists().indexOf(ul); }

  setDropdownLabel(ul, text) {
    const host = ul.closest('product-option-picker') || ul.parentElement;
    if (!host) return;
    const node = host.querySelector('.product-option-picker-label .product-option-picker-label--chosen')
              || host.querySelector('.product-option-picker-chosen > span');
    if (node) node.textContent = text || this.PLACEHOLDER;
  }

  // ALSO put the text into the dropdown “box” so it never looks blank
  // ALSO put the text into the dropdown “box” so it never looks blank
setDropdownBoxValue(ul, text) {
  const host = ul.closest('product-option-picker') || ul.parentElement;
  if (!host) return;

  // Update data-current attribute (for CSS or debugging)
  const dd = host.querySelector('.product-option-picker__dropdown');
  if (dd) dd.dataset.current = (text || this.PLACEHOLDER);

  // Write directly into the visible value span
  const valueNode = host.querySelector('.product-option-picker-chosen__value');
  if (valueNode) valueNode.textContent = (text || this.PLACEHOLDER);
}


  syncLabelsFromChosen() {
    this.getLists().forEach(ul => {
      const li = ul.querySelector('li.chosen');
      const text = li ? li.textContent.trim() : this.PLACEHOLDER;
      this.setDropdownLabel(ul, text);
      this.setDropdownBoxValue(ul, text);
    });
  }

  /* -------------------- utilities (variant/ATC) -------------------- */
  getIdInput() {
    return (this.form || this.section).querySelector('input[name="id"]');
  }

  setHiddenVariantId(variantId) {
    const idInput = this.getIdInput();
    if (idInput) idInput.value = variantId || '';
  }

  clearVariantAndDisableATC() {
    this.setHiddenVariantId('');
    const atc = this.section.querySelector('button[type="submit"], .product-form__submit, .add-to-cart');
    if (atc) { atc.setAttribute('disabled','disabled'); atc.classList.add('is-disabled'); }
  }

  enableATC() {
    const atc = this.section.querySelector('button[type="submit"], .product-form__submit, .add-to-cart');
    if (atc) { atc.removeAttribute('disabled'); atc.classList.remove('is-disabled'); }
  }

  resetFollowing(startIndex) {
    const lists = this.getLists();
    for (let i = startIndex + 1; i < lists.length; i++) {
      const ul = lists[i];
      ul.querySelectorAll('li.chosen').forEach(li => li.classList.remove('chosen'));
      this.setDropdownLabel(ul, this.PLACEHOLDER);
      this.setDropdownBoxValue(ul, this.PLACEHOLDER);
      const sw = (ul.closest('product-option-picker') || ul.parentElement)?.querySelector('.product-option-picker-chosen > .product-option-swatch');
      if (sw) { sw.innerHTML = ''; sw.style.removeProperty('--product-swatch-value'); }
    }
  }

  autoPickFollowing(startIndex) {
    const lists = this.getLists();
    let picked = false;
    for (let i = startIndex + 1; i < lists.length; i++) {
      const ul = lists[i];
      const firstAvailable = ul.querySelector('li.product-option-picker__list-item:not(.disabled)');
      if (firstAvailable) {
        this.selectionPicker(firstAvailable);
        picked = true;
      } else {
        this.setDropdownLabel(ul, this.PLACEHOLDER);
        this.setDropdownBoxValue(ul, this.PLACEHOLDER);
      }
    }
    this.syncLabelsFromChosen();
    return picked;
  }

  buildChosenValues() {
    const ids = [];
    this.getLists().forEach(list => {
      const chosenInput = list.querySelector('li.chosen input');
      if (chosenInput) ids.push(chosenInput.dataset.id);
    });
    return ids.join(',');
  }

  finalizeUrlAndATC(newUrl, variantId) {
    if (variantId) {
      this.setHiddenVariantId(variantId);   // ← CRITICAL: keep ATC form in sync
      if (newUrl.searchParams.get('variant') !== null) newUrl.searchParams.set('variant', variantId);
      else newUrl.searchParams.append('variant', variantId);
      this.enableATC();
    } else {
      newUrl.searchParams.delete('variant');
      this.clearVariantAndDisableATC();
    }
    if (!Shopify.designMode) history.pushState({}, null, newUrl);
  }

  /* -------------------- main flow -------------------- */
  onClick(option) {
    const currentUl   = option.closest('ul');
    const changedIdx  = this.getIndex(currentUl);

    // 1) choose in current dropdown
    this.selectionPicker(option);

    // 2) reset later dropdowns to placeholder
    this.resetFollowing(changedIdx);

    // 3) fetch with partial choices to update availability
    const optionProductUrl = option.querySelector('input').dataset.productUrl;
    const chosenValues     = this.buildChosenValues();
    const url              = optionProductUrl + '?section_id=' + this.sectionId + '&option_values=' + chosenValues;

    fetch(url)
      .then(res => res.text())
      .then(response => {
        const parsed = new DOMParser().parseFromString(response, 'text/html');
        let newUrl;

        if (this.productUrl !== optionProductUrl) {
          this.section.innerHTML = parsed.querySelector('.main-product').innerHTML;
          this.productUrl = optionProductUrl;
          newUrl = new URL(window.location.origin + optionProductUrl);
        } else {
          const product_options     = parsed.querySelector('product-options');
          const product_form        = parsed.querySelector('.product-summary__product-form');
          const pickup_availability = parsed.querySelector('pickup-availability');

          if (pickup_availability && this.section.querySelector('pickup-availability')) {
            this.section.querySelector('pickup-availability').innerHTML = pickup_availability.innerHTML;
          }
          if (product_form && this.section.querySelector('.product-summary__product-form')) {
            this.section.querySelector('.product-summary__product-form').innerHTML = product_form.innerHTML;
          }
          if (product_options && this.section.querySelector('product-options')) {
            this.section.querySelector('product-options').innerHTML = product_options.innerHTML;
          }
          newUrl = new URL(window.location.href);
        }

        // 4) rebind refs
        this.refreshRefs();

        // 5) auto-pick first available for following options (prevents blanks)
        const picked = this.autoPickFollowing(changedIdx);

        // 6) resolve final variant id (after auto-pick)
        const chosenValuesAfter = this.buildChosenValues();
        const resolveUrl = optionProductUrl + '?section_id=' + this.sectionId + '&option_values=' + chosenValuesAfter;

        fetch(resolveUrl)
          .then(r => r.text())
          .then(text2 => {
            const parsed2   = new DOMParser().parseFromString(text2, 'text/html');
            const variantId = parsed2.querySelector('input[name="id"]')?.value || null;

            // 7) keep URL & hidden id in sync; update ATC state
            this.finalizeUrlAndATC(newUrl, variantId);

            // 8) final UI sync so boxes never look blank
            this.syncLabelsFromChosen();
          })
          .catch(() => {
            this.finalizeUrlAndATC(newUrl, null);
            this.syncLabelsFromChosen();
          });
      })
      .catch(() => { /* noop, keep UI responsive */ });
  }

  selectionPicker(option) {
    const ul   = option.parentElement; // UL
    const host = ul.closest('product-option-picker') || ul.parentElement;

    ul.querySelectorAll('li').forEach(li => li.classList.remove('chosen'));
    option.classList.add('chosen');

    const text = option.textContent.trim();
    this.setDropdownLabel(ul, text);
    this.setDropdownBoxValue(ul, text);

    const chosenSwatchElement = host.querySelector('.product-option-picker-chosen > .product-option-swatch');
    const chosenOptionSwatch  = option.querySelector('.product-option-swatch');
    if (chosenSwatchElement) {
      if (chosenOptionSwatch) {
        const cs = getComputedStyle(chosenOptionSwatch);
        let color = cs.getPropertyValue('--product-swatch-value')?.trim();
        if (!color) color = option.dataset.optionValue || '';
        chosenSwatchElement.innerHTML = chosenOptionSwatch.innerHTML;
        if (color) chosenSwatchElement.style.setProperty('--product-swatch-value', color);
        else chosenSwatchElement.style.removeProperty('--product-swatch-value');
      } else {
        chosenSwatchElement.innerHTML = '';
        chosenSwatchElement.style.removeProperty('--product-swatch-value');
      }
    }
  }

  /* -------------------- dropdowns -------------------- */
  dropdown(dropdown) {
    if (dropdown.classList.contains('active')) { this.closeDropdown(dropdown); dropdown.blur(); }
    else { if (!dropdown.matches(':focus-within:not(:focus)')) this.openDropdown(dropdown); }
  }
  openDropdown(dropdown){ dropdown.classList.add('active'); }
  closeDropdown(dropdown){ dropdown.classList.remove('active'); }
}

customElements.define('product-options', ProductOptionPicker);
}